import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Scale, Plus, X, CheckCircle, AlertTriangle, Loader2, Send } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ChallengeForm({ analysisId, onSubmit, onCancel }) {
  const { token, user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const [proofs, setProofs] = useState([{ proof_type: 'url', content: '', description: '' }]);
  const [argument, setArgument] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const addProof = () => {
    if (proofs.length < 5) setProofs([...proofs, { proof_type: 'url', content: '', description: '' }]);
  };

  const removeProof = (index) => {
    if (proofs.length > 1) setProofs(proofs.filter((_, i) => i !== index));
  };

  const updateProof = (index, field, value) => {
    const newProofs = [...proofs];
    newProofs[index][field] = value;
    setProofs(newProofs);
  };

  const handleSubmit = async () => {
    if (!argument.trim() || proofs.some(p => !p.content.trim())) {
      setError(t('challenge_fill_fields'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(`${API_URL}/api/challenge`, {
        analysis_id: analysisId,
        proofs,
        user_argument: argument
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setResult(res.data);
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || t('challenge_error'));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        className="mt-4 p-4 rounded-xl bg-slate-800/50 border border-white/10"
      >
        <div className="flex items-center gap-3 mb-4">
          {result.challenge_accepted ? (
            <CheckCircle className="w-6 h-6 text-emerald-400" />
          ) : (
            <X className="w-6 h-6 text-rose-400" />
          )}
          <h4 className={`font-semibold ${result.challenge_accepted ? 'text-emerald-400' : 'text-rose-400'}`}>
            {result.challenge_accepted ? t('challenge_accepted') : t('challenge_rejected')}
          </h4>
        </div>

        <p className="text-slate-300 text-sm mb-3">{result.explanation}</p>

        {result.challenge_accepted && (
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <p className="text-emerald-400 text-sm">
              {t('challenge_new_verdict')}: <strong className="capitalize">{result.updated_verdict.replace('_', ' ')}</strong> ({result.updated_confidence}%)
            </p>
          </div>
        )}

        <div className="mt-4">
          <h5 className="text-white text-sm font-medium mb-2">{t('challenge_proof_analysis')}:</h5>
          <div className="space-y-2">
            {result.proofs_analysis.map((proof, idx) => (
              <div key={idx} className="p-2 rounded bg-white/5 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  {proof.proof_authentic ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-rose-400" />
                  )}
                  <span className="text-white">{t('challenge_proof')} {idx + 1}: {proof.proof_authentic ? t('challenge_authentic') : t('challenge_unverified')}</span>
                  <span className="text-slate-500">({proof.proof_reliability_score}%)</span>
                </div>
                {proof.manipulation_indicators && proof.manipulation_indicators.length > 0 && (
                  <p className="text-rose-400 text-xs ml-6">{proof.manipulation_indicators.join(', ')}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        <button onClick={onCancel} className="mt-4 w-full py-2 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 transition-colors">
          {t('challenge_close')}
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="mt-4 p-4 rounded-xl bg-slate-800/50 border border-white/10"
    >
      <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
        <Scale className="w-5 h-5 text-amber-400" />
        {t('challenge_title')}
      </h4>

      <p className="text-slate-400 text-sm mb-4">
        {t('challenge_desc')}
      </p>

      <div className="space-y-4 mb-4">
        {proofs.map((proof, idx) => (
          <div key={idx} className="p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white text-sm font-medium">{t('challenge_proof')} {idx + 1}</span>
              {proofs.length > 1 && (
                <button onClick={() => removeProof(idx)} className="p-1 rounded hover:bg-white/10 text-slate-400">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 mb-2">
              {['url', 'text', 'image'].map(type => (
                <button
                  key={type}
                  onClick={() => updateProof(idx, 'proof_type', type)}
                  className={`py-1 px-2 rounded text-xs transition-colors ${
                    proof.proof_type === type
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-white/5 text-slate-400 hover:bg-white/10'
                  }`}
                >
                  {type === 'url' ? 'URL' : type === 'text' ? t('input_text') : 'Image'}
                </button>
              ))}
            </div>

            <input
              type="text"
              value={proof.content}
              onChange={(e) => updateProof(idx, 'content', e.target.value)}
              placeholder={proof.proof_type === 'url' ? 'https://...' : t('challenge_proof_content_ph')}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:border-emerald-500/50 mb-2"
            />

            <input
              type="text"
              value={proof.description}
              onChange={(e) => updateProof(idx, 'description', e.target.value)}
              placeholder={t('challenge_proof_desc_ph')}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:border-emerald-500/50"
            />
          </div>
        ))}
      </div>

      {proofs.length < 5 && (
        <button
          onClick={addProof}
          className="w-full py-2 rounded-lg border border-dashed border-white/20 text-slate-400 hover:border-emerald-500/50 hover:text-emerald-400 transition-colors text-sm flex items-center justify-center gap-2 mb-4"
        >
          <Plus className="w-4 h-4" />
          {t('challenge_add_proof')}
        </button>
      )}

      <div className="mb-4">
        <label className="block text-white text-sm font-medium mb-2">{t('challenge_argument')}</label>
        <textarea
          value={argument}
          onChange={(e) => setArgument(e.target.value)}
          placeholder={t('challenge_argument_ph')}
          rows={3}
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:border-emerald-500/50 resize-none"
        />
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20">
          <p className="text-rose-400 text-sm">{error}</p>
        </div>
      )}

      {user && user.credits < 3 && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <p className="text-amber-400 text-sm">{t('challenge_credits_low', { n: user.credits })}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-2 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 transition-colors">
          {t('challenge_cancel')}
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading || (user && user.credits < 3)}
          className="flex-1 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {t('challenge_submit')}
        </button>
      </div>
    </motion.div>
  );
}
